"use client";

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useSyncExternalStore,
} from "react";

import type { LibraryRestore, MovieId } from "@/types/library";
import type { LibraryState, MovieLibraryState } from "@/lib/library/state";
import type { LibraryStore, MutationOutcome } from "@/lib/library/store";

export const LibraryContext = createContext<LibraryStore | null>(null);

/** The app-wide library store (see `<LibraryProvider>`). */
export function useLibraryStore(): LibraryStore {
  const store = useContext(LibraryContext);

  if (!store) {
    throw new Error("useLibraryStore() must be used inside <LibraryProvider>");
  }

  return store;
}

export interface UseLibraryResult extends LibraryState {
  watchlist: LibraryState["library"]["watchlist"];
  watched: LibraryState["library"]["watched"];
  ratings: LibraryState["library"]["ratings"];
  addToWatchlist: (movieId: MovieId) => Promise<MutationOutcome>;
  removeFromWatchlist: (movieId: MovieId) => Promise<MutationOutcome>;
  markWatched: (movieId: MovieId) => Promise<MutationOutcome>;
  unmarkWatched: (movieId: MovieId) => Promise<MutationOutcome>;
  rate: (movieId: MovieId, rating: number) => Promise<MutationOutcome>;
  removeRating: (movieId: MovieId) => Promise<MutationOutcome>;
  restore: (
    movieId: MovieId,
    entry: LibraryRestore,
  ) => Promise<MutationOutcome>;
  revalidate: (options?: { force?: boolean }) => Promise<void>;
}

/**
 * The signed-in user's library (watchlist, watched movies and ratings with
 * timestamps), its status and the functions that change it.
 *
 * The mutation functions are "raw": for buttons, prefer `useLibraryActions()`
 * which adds toasts, undo and analytics.
 */
export function useLibrary(): UseLibraryResult {
  const store = useLibraryStore();
  const state = useSyncExternalStore(
    store.subscribe,
    store.getState,
    store.getServerState,
  );
  const mutations = useMemo(
    () => ({
      addToWatchlist: (movieId: MovieId) => store.addToWatchlist(movieId),
      removeFromWatchlist: (movieId: MovieId) =>
        store.removeFromWatchlist(movieId),
      markWatched: (movieId: MovieId) => store.markWatched(movieId),
      unmarkWatched: (movieId: MovieId) => store.unmarkWatched(movieId),
      rate: (movieId: MovieId, rating: number) => store.rate(movieId, rating),
      removeRating: (movieId: MovieId) => store.removeRating(movieId),
      restore: (movieId: MovieId, entry: LibraryRestore) =>
        store.restore(movieId, entry),
      revalidate: (options?: { force?: boolean }) => store.revalidate(options),
    }),
    [store],
  );

  return useMemo(
    () => ({
      ...state,
      watchlist: state.library.watchlist,
      watched: state.library.watched,
      ratings: state.library.ratings,
      ...mutations,
    }),
    [state, mutations],
  );
}

/**
 * One movie's library state. Only re-renders when that movie changes, so it
 * is cheap to use in every card of a long grid.
 */
export function useMovieLibraryState(movieId: MovieId): MovieLibraryState {
  const store = useLibraryStore();
  const getSnapshot = useCallback(
    () => store.getMovieState(movieId),
    [store, movieId],
  );

  return useSyncExternalStore(
    store.subscribe,
    getSnapshot,
    store.getServerMovieState,
  );
}
